/*
 * Nosto Common JS
 * Handles initialization of Nosto recommendations, fetching data from Nosto API,
 * and rendering product recommendations across the site.
 */

/**
 * Initialize Nosto recommendations by fetching data from Nosto API for all placement elements
 */
async function initNostoRecommendations() {
  const nostoElements = Array.from(document.querySelectorAll('nosto-placement:not(drawer-cart .nosto-upsell)'));
  // Push the minicart element last to render it last
  const nostoCartElement = document.querySelector('drawer-cart .nosto-upsell');
  if (nostoCartElement) {
    nostoElements.push(nostoCartElement);
  }
  if (!nostoElements || nostoElements.length === 0) return;

  const placements = Array.from(nostoElements).map(el => el.getAttribute('data-placement-id')).filter(Boolean);

  if (placements.length === 0) return;
  // Utility function to find and handle Nosto elements
  const handleNostoElement = (placementId, action) => {
    const nostoElement = document.querySelector(`nosto-placement[data-placement-id="${placementId}"]`);
    if (nostoElement) action(nostoElement);
  };

  // Remove loading state for all placements
  const removeLoadingStates = () => {
    placements.forEach(placementId => {
      handleNostoElement(placementId, element => element.removeLoadingState());
    });
  };

  const response = await new Promise(resolve => {
    nostojs(async api => {
      const results =
        api
          .createRecommendationRequest({ includeTagging: true })
          .setResponseMode("JSON_ORIGINAL")
          .addElements(placements)
          .load()
      resolve(results);
    })
  });
  const recommendations = response?.recommendations;
  if (!recommendations) {
    removeLoadingStates();
    return;
  }

  // Process each placement
  placements.forEach(placementId => {
    const products = recommendations[placementId]?.products;

    handleNostoElement(placementId, element => {
      if (products && products.length > 0) {
        // Limit products based on maxProducts option if specified
        const maxProducts = element.maxProducts;
        const limitedProducts = maxProducts ? products.slice(0, maxProducts) : products;

        element.generateProductsMarkup(limitedProducts)
          .then(markup => {
            if (element.productGrid) {
              element.productGrid.innerHTML = markup;
              element.gridySlider?.update();
            }
            element.removeLoadingState();
          })
          .catch(err => {
            console.error(`Error generating markup for placement ${placementId}:`, err);
            element.removeLoadingState();
            element.hideWrapper();
          });
      } else {
        element.removeLoadingState();
        element.hideWrapper()
      }
    });
  });
}

/**
 * Updates Nosto cart data and refreshes recommendations after product is added to cart
 */
async function updateNostoAfterCartAdd() {
  await Nosto.reloadCartWithFlags({ skipEvents: true })
  initNostoRecommendations();
}

/**
 * NostoPlacement custom element - handles individual placement rendering
 */
class NostoPlacement extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.loadingSpinner = this.querySelector('.loading-overlay');

    // Parse options from data attribute
    this.options = JSON.parse(this.getAttribute('data-options') || '{}');

    // Extract maxProducts from options
    this.maxProducts = this.options.maxProducts ? parseInt(this.options.maxProducts) : null;

    // Determine if this is an upsell or regular placement
    this.isUpsell = this.classList.contains('nosto-upsell');
    this.productGrid = this.querySelector('[data-product-grid-nosto]');
    this.container = this;
    this.wrapper = this.closest('.upsell-wrapper');

    // Initialize slider-related properties
    this.gridySlider = this.querySelector('gridy-slider');

    // Get iprDesktop from options first, then fallback to slider attribute if available
    this.iprDesktop = this.options.iprDesktop ?
      parseInt(this.options.iprDesktop) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprDesktop) : null);

    // Get iprTablet from options first, then fallback to slider attribute if available
    this.iprTablet = this.options.iprTablet ?
      parseInt(this.options.iprTablet) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprTablet) : null);

    // Get iprMobile from options first, then fallback to slider attribute if available
    this.iprMobile = this.options.iprMobile ?
      parseInt(this.options.iprMobile) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprMobile) : null);

    this.arrows = this.querySelector('.gridy-arrows');

    // Set initial loading state
    this.setLoadingState();

    // Set up context for tracking
    this.context = 'recommendation';
    this.placementId = this.getAttribute('data-placement-id');
    this.slotId = this.getAttribute('data-slot-id') || this.placementId;

    // Set up click tracking for product cards
    this.setupClickTracking();
  }

  /**
   * Set up click tracking for product cards using event delegation
   */
  setupClickTracking() {
    if (this.productGrid) {
      this.productGrid.addEventListener('click', this.handleProductClick.bind(this));
    }
  }

  /**
   * Handle click events on product cards and track via Nosto API
   * @param {Event} event - Click event
   */
  handleProductClick(event) {
    // Find the closest product card element
    const card = event.target.classList.contains('.card--product') ? event.target : event.target.closest('.card--product');
    if (!card) return;

    // Extract product data from the clicked card
    const productId = card.getAttribute('data-product-id') || card.getAttribute('data-track-product-id');
    const productUrl = `${window.location.origin}${card.querySelector('a')?.getAttribute('href')}`;

    if (this.context === 'recommendation' && this.placementId && productId) {
      this.trackRecommendationClick(productId);
    } else if (productId && productUrl) {
      this.trackSearchClick(productId, productUrl);
    }
  }

  /**
   * Track when a product in a recommendation is clicked
   * @param {string} productId - ID of the product that was clicked
   */
  trackRecommendationClick(productId) {
    if (!this.slotId || !window.nostojs) return;
    nostojs(api => {
      api.createRecommendationRequest()
        .setProducts([{ product_id: productId }], this.slotId)
        .load();
    });
  }

  /**
   * Track when a product in search results is clicked
   * @param {string} productId - ID of the product that was clicked
   * @param {string} productUrl - URL of the product page
   */
  trackSearchClick(productId, productUrl) {
    if (!window.nostojs) return;
    nostojs(api => {
      api.recordSearchClick(
        this.context,
        {
          productId: productId,
          url: productUrl
        }
      );
      api.createRecommendationRequest()
        .setProducts([{ product_id: productId }])
        .load({ skipPageViews: true });
    });
  }

  /**
   * Generate HTML markup for products returned from Nosto API
   * @param {Array} products - Array of product data from Nosto
   * @returns {Promise<string>} HTML markup for the product grid
   */
  async generateProductsMarkup(products) {
    if (!products || !Array.isArray(products)) {
      console.warn('Invalid products data provided to generateProductsMarkup');
      return '';
    }

    // Get template content
    const template = this.isUpsell ? document.querySelector('.js-card-product-template-upsell') : document.querySelector('.js-card-product-template');
    if (!template) {
      console.warn('Product card template not found');
      return '';
    }

    const productsMarkup = products.map((product, index) => {
      if (!product) return '';

      const templateContent = template.content.cloneNode(true);
      const container = document.createElement('div');
      container.appendChild(templateContent);

      try {
        // Extract product data with fallbacks
        const productHandle = product.url?.split('/').slice(-1)[0] || '';
        const productUrlRelative = productHandle ? `/products/${productHandle}` : '#';
        const productPrice = product.price ? product.price : '';
        const productPriceWithFormatting = productPrice ? Woolman.Utils.formatMoney(productPrice * 100) : '';
        const productListPrice = product.listPrice || product.list_price || '';
        const productListPriceWithFormatting = productListPrice ? Woolman.Utils.formatMoney(productListPrice * 100) : '';
        const onDiscount = productListPrice && productPrice < productListPrice;
        const productThumbUrl = product.imageUrl || product.image_url || '';
        const productName = product.name || product.product_name || 'Product';
        const productBrand = product.brand || product.vendor || '';
        const productId = product.productId || product.product_id || '';
        const customFields = product.customFields || product.custom_fields || {};

        // Replace placeholders
        container.innerHTML = container.innerHTML
          .replace(/\[\[product\.url\]\]/g, productUrlRelative)
          .replace(/\[\[product\.id\]\]/g, productId)
          .replace(/\[\[product\.handle\]\]/g, productHandle);

        const cardType = this.options.cardType;
        const cardTextAlign = this.options.cardTextAlign;

        const cardElement = container.querySelector('.card--product');
        cardElement.setAttribute('data-index', index + 1);
        if (cardType) {
          cardElement.classList.add(`card--${cardType}`);
        }
        cardElement.querySelector('.card-body').classList.add(cardTextAlign);
        const productDataSpan = document.createElement('span');
        productDataSpan.className = 'hidden';
        productDataSpan.setAttribute('data-product-json', productId);
        productDataSpan.innerHTML = JSON.stringify(product);
        cardElement.appendChild(productDataSpan);

        const productAriaLabel = window.accessibilityStrings.viewSomething.replace('{{ something }}', productName);
        container.querySelector('.card-media').setAttribute('aria-label', productAriaLabel);
        container.querySelector('.card-title a').setAttribute('aria-label', productAriaLabel);

        // Set primary image for normal product cards
        this._setProductImage(container, productThumbUrl, productName, index);

        // Set badges
        this._setProductBadges(container, customFields);

        // Set product title
        const titleElement = container.querySelector('[data-product-title]');
        if (titleElement) {
          titleElement.textContent = productName;
        }

        // Set vendor/brand
        const vendorElement = container.querySelector('[data-vendor]');
        if (vendorElement && productBrand) {
          vendorElement.textContent = productBrand;
        }

        // Set price
        this._setProductPrice(container, productPriceWithFormatting, productListPriceWithFormatting, onDiscount);

        // Add fade-in class with staggered delay
        if (cardElement) {
          // Add a small delay based on index for staggered animation
          cardElement.style.animationDelay = `${index * 0.05}s`;
          cardElement.classList.add('fade-in');
          cardElement.setAttribute("data-track-click", "product_card_click");
          cardElement.setAttribute("data-track-product-id", productId);
        }

        return container.innerHTML;
      } catch (error) {
        console.error('Error processing product markup:', error);
        return '';
      }
    }).join('');

    // Handle arrows visibility based on product count
    this._handleArrowsVisibility(products);

    return productsMarkup;
  }

  /**
   * Helper method to set product image
   */
  _setProductImage(container, thumbUrl, productName, index) {
    const primaryImageContainer = container.querySelector('.card-media');
    if (primaryImageContainer && thumbUrl) {
      const figureElement = document.createElement('figure');
      const cardImgAspectRatio = this.options.cardImgAspectRatio;
      const cardImgSizingRule = this.options.cardImgSizingRule;
      figureElement.className = `media ${cardImgSizingRule} ${cardImgAspectRatio}`;

      // Determine loading strategy:
      // 1. Always use lazy loading for all images in gridySlider
      // 2. If no slider is used, load the first visible products eagerly based on iprDesktop value
      // 3. Default to loading first 4 products eagerly if iprDesktop is not available
      let loadingStrategy = 'lazy';
      let fetchPriority = 'low';

      if (!this.gridySlider) {
        // Use iprDesktop value to determine how many products to load eagerly
        // This matches the number of products visible in the first row
        const visibleItemsCount = this.iprDesktop || 4;
        loadingStrategy = index < visibleItemsCount ? 'eager' : 'lazy';
        fetchPriority = index < visibleItemsCount ? 'high' : 'low';
      }

      // Create srcset with multiple resolutions
      const srcset = [
        `${thumbUrl}&width=90 90w`,
        `${thumbUrl}&width=180 180w`,
        `${thumbUrl}&width=280 280w`,
        `${thumbUrl}&width=390 390w`,
        `${thumbUrl}&width=540 540w`,
        `${thumbUrl}&width=768 768w`,
        `${thumbUrl}&width=1080 1080w`,
        `${thumbUrl}&width=1280 1280w`,
        `${thumbUrl}&width=1500 1500w`
      ].join(', ');

      figureElement.innerHTML = `
            <img
              srcset="${srcset}"
              sizes="100vw"
              src="${thumbUrl}"
              alt="${productName}"
              loading="${loadingStrategy}"
              fetchpriority="${fetchPriority}"
              decoding="async"
              onload="this.classList.add('loaded')"
            >
          `;

      primaryImageContainer.insertBefore(figureElement, primaryImageContainer.firstChild);
    }
  }

  /**
   * Helper method to set product badges
   */
  _setProductBadges(container, customFields) {
    try {
      let customBadges;
      if (Array.isArray(customFields)) {
        customBadges = customFields.find(f => f.key === 'woolman-custom_badges')?.value;
      } else if (typeof customFields === 'object' && customFields !== null) {
        customBadges = customFields['woolman-custom_badges'];
      }

      if (customBadges && customBadges.length > 0) {
        customBadges = typeof customBadges === 'string' ? JSON.parse(customBadges) : customBadges;
        const badgesElement = container.querySelector('[data-product-badges]');
        if (badgesElement && window.badges) {
          const badgeMarkup = customBadges.map(badgeName => {
            const badge = window.badges[badgeName];
            if (!badge) return '';
            return `
                  <span
                    class="badge ${badgeName.toLowerCase()}"
                    style="
                      --text-color: ${badge.text_color};
                      --background-color: ${badge.background_color};
                    "
                  >
                    ${badge.label}
                  </span>
                `;
          }).join('');
          badgesElement.innerHTML = badgeMarkup;
        }
      }
    } catch (error) {
      console.error('Error setting product badges:', error);
    }
  }

  /**
   * Helper method to set product price
   */
  _setProductPrice(container, price, listPrice, onDiscount) {
    const priceElement = container.querySelector('[data-price]');
    let salePriceHtml = '';
    if (onDiscount) {
      priceElement.classList.add('price--on-sale');
      salePriceHtml = `
    <span class="visually-hidden">Sale price</span>
    <span class="price-item price-item--sale price-item--last">${price}</span>
    `
    }
    if (priceElement) {
      priceElement.innerHTML = `
          <div class="flex">
            <div class="price__regular">
              <span class="visually-hidden">Regular price</span>
              <span class="price-item price-item--regular">${price}</span>
            </div>
            ${listPrice ? `
              <div class="price__sale">
                <span class="visually-hidden">Regular price</span>
                <span>
                  <s class="price-item price-item--regular style--subdued">${listPrice}</s>
                  ${salePriceHtml}
                </span>
              </div>
            ` : ''}
          </div>
        `;
    }
  }

  _generateContentSlotElement(contentSlot) {
    const contentSlotUrl = contentSlot.link;
    const contentSlotImageAlt = contentSlot.imageAltText || '';
    const contentSlotImageUrl = contentSlot.image || '';
    const contentSlotPosition = contentSlot.position;

    const contentCardTemplate = document.querySelector('.js-card-content-template');
    const contentCardTemplateContent = contentCardTemplate.content.cloneNode(true);
    const contentCardContainer = document.createElement('div');
    contentCardContainer.appendChild(contentCardTemplateContent);
    if (contentSlotUrl)
      contentCardContainer.innerHTML = contentCardContainer.innerHTML
        .replace(/\[\[product\.url\]\]/g, contentSlotUrl)
    const contentAriaLabel = window.accessibilityStrings.viewSomething.replace('{{ something }}', contentSlotImageAlt);
    contentCardContainer.querySelector('.card-media').setAttribute('aria-label', contentAriaLabel);
    this._setProductImage(contentCardContainer, contentSlotImageUrl, contentSlotImageAlt, contentSlotPosition);
    return contentCardContainer.querySelector('.card--content-slot');
  }

  /**
   * Helper method to handle slider arrows visibility
   */
  _handleArrowsVisibility(products) {
    try {
      if (this.gridySlider && this.iprDesktop && this.arrows) {
        if (products.length <= this.iprDesktop) {
          this.arrows.style.display = 'none';
        } else {
          // Ensure arrows are shown if there are more products than iprDesktop
          this.arrows.style.display = '';
        }
      }
    } catch (error) {
      console.error('Error handling arrows visibility:', error);
    }
  }

  /**
   * Set loading state for this placement
   */
  setLoadingState() {
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.remove('hidden');
    }
    if (this.container) {
      this.container.setAttribute('loading', '');
    }
    if (this.productGrid) {
      this.productGrid.setAttribute('loading', '');
    }
  }

  /**
   * Remove loading state for this placement
   */
  removeLoadingState() {
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.add('hidden');
    }
    if (this.container) {
      this.container.removeAttribute('loading');
    }
    if (this.productGrid) {
      this.productGrid.removeAttribute('loading');
    }
  }

  /**
   * Hide the wrapper element for this placement
   */
  hideWrapper() {
    if (this.wrapper) {
      this.wrapper.classList.add('hidden');
    }
    if (this.container) {
      this.container.classList.add('hidden');
    }
    if (this.productGrid) {
      this.productGrid.classList.add('hidden');
    }
  }

  /**
   * Track product added to cart from recommendation
   * @param {string} productId - Product ID that was added
   */
  trackRecommendationAddedToCart(productId) {
    if (!this.placementId || !window.nostojs) return;
    nostojs(api => {
      api.recommendedProductAddedToCart(productId, this.placementId);
    });
  }

  /**
   * Track variant change in recommendation product
   * @param {string} productId - Product ID
   * @param {string} variantId - Selected variant ID
   */
  trackRecommendationVariantChange(productId, variantId) {
    if (!this.placementId || !window.nostojs) return;
    nostojs(api => {
      api.createRecommendationRequest({ includeTagging: true })
        .setProducts([{ product_id: productId, selected_sku_id: variantId }])
        .load();
    });
  }
}

// Register custom element if not already registered
if (!customElements.get('nosto-placement')) {
  customElements.define('nosto-placement', NostoPlacement);
}

class NostoSearch extends NostoPlacement {
  constructor() {
    super();
    // Initialize class properties with better error handling
    this.searchForm = this.querySelector('form[role="search"]');
    this.input = this.querySelector('input[type="search"]');
    this.resultsCount = this.querySelector('#products-count-ajax');
    this.header = this.querySelector('header h1');
    this.facetsFormContainer = document.querySelector('.filter-form');
    this.abortController = new AbortController();
    this.sortBy = this.querySelector('#sort-by');
    this.viewMoreButton = this.querySelector('#pagination-ajax button');
    this.showCount = this.querySelector('[data-show-count]');
    this.resultsSize = parseInt(this.productGrid?.getAttribute('data-results-per-page')) || 24;
    this.context = this.productGrid?.getAttribute('data-context');
    this.collectionId = this.productGrid?.getAttribute('data-collection-id') || null;
    this.collectionTitle = this.productGrid?.getAttribute('data-collection') || null;
    this.searchTerm = '';
    this.searchableFacets = ['brand'];
    this.openedFacets = [];
    this.activeMaxPrice = null;
    this.lastEditedFacet = null;
    // Inclusion list to show facets only for specific divisions. Rule is key: d365.division value: facet name
    this.includeFacetRules = { 'beauty': 'sub-category' };
    this.excludeFacets = [];
    if (this.context === 'serp') {
      this.excludeFacets.push('sub-category');
    }

    // Improved fallback handling with default values
    this.enableFallback = JSON.parse(this.options.enableFallback || 'false');
    this.usingFallback = false;
    this.fallbackAttemptTime = null;

    // Add retry counter for error handling
    this.retryCount = 0;
    this.maxRetries = 3;

    // Check required elements
    if (!this.productGrid) {
      console.error('Nosto: Required product grid element not found');
      return;
    }

    //this.getNostoSearchResults();
    this.setupEventListeners();
    this.initializeFromURL();
  }

  updatePaginationState() {
    const total = parseInt(this.productGrid?.getAttribute('data-filtered-count')) || 0;
    const totalPages = Math.ceil(total / this.resultsSize);

    // Update SEO pagination links
    const seoPagination = document.querySelector('.seo-pagination');
    if (seoPagination) {
      seoPagination.innerHTML = this.generatePaginationLinks(this.currentPage, totalPages);
    }

    // Update meta tags
    this.updatePaginationMeta(this.currentPage, totalPages);
  }

  generatePaginationLinks(currentPage, totalPages) {
    const baseUrl = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.delete('view')
    let links = '';

    if (currentPage > 1) {
      if (currentPage - 1 === 1) {
        params.delete('page');
      } else {
        params.set('page', currentPage - 1);
      }
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      links += `<a href="${baseUrl}${paramsStr}" rel="prev">Previous</a>`;
    }

    if (currentPage < totalPages) {
      params.set('page', currentPage + 1);
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      links += `<a href="${baseUrl}${paramsStr}" rel="next">Next</a>`;
    }

    return links;
  }

  updatePaginationMeta(currentPage, totalPages) {
    // Remove existing rel meta tags
    document.querySelectorAll('link[rel="prev"], link[rel="next"]').forEach(el => el.remove());

    const baseUrl = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.delete('view');

    // Add prev meta tag if not on first page
    if (currentPage > 1) {
      if (currentPage - 1 === 1) {
        params.delete('page');
      } else {
        params.set('page', currentPage - 1);
      }
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      const prevLink = document.createElement('link');
      prevLink.rel = 'prev';
      prevLink.href = `${baseUrl}${paramsStr}`;
      document.head.appendChild(prevLink);
    }

    // Add next meta tag if not on last page
    if (currentPage < totalPages) {
      params.set('page', currentPage + 1);
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      const nextLink = document.createElement('link');
      nextLink.rel = 'next';
      nextLink.href = `${baseUrl}${paramsStr}`;
      document.head.appendChild(nextLink);
    }
  }

  /**
   * Set up initial state from URL parameters
   */
  initializeFromURL() {
    const params = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(params.get('page')) || 1;

    // Handle keyword parameter
    const isKeyword = params.get('keyword') === 'true';
    if (isKeyword) {
      params.set('keyword', 'true');
    }

    // Update URL and sort options
    this.updateQuery(params);
    if (params.has('sort_by') && this.sortBy) {
      this.sortBy.value = params.get('sort_by');
    }

    // Set loading state and fetch results
    this.setLoadingState();
    this.getSearchResults();
    this.updatePaginationState();
  }

  /**
   * Set up all event listeners for the search component
   */
  setupEventListeners() {
    // Store bound event handlers as class properties
    this.boundFacetChangeHandler = this.onFacetChange.bind(this);
    this.boundFacetClearHandler = this.onFacetClear.bind(this);

    // Use optional chaining to prevent errors if elements don't exist
    this.searchForm?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.sortBy?.addEventListener('change', this.onSelectChange.bind(this));
    this.viewMoreButton?.addEventListener('click', this.onViewMoreClick.bind(this));
  }

  /**
   * Clean up event listeners when element is removed
   */
  disconnectedCallback() {
    // Clean up abort controller
    if (this.abortController) {
      this.abortController.abort();
    }

    // Remove event listeners from facet elements
    this.facetsFormContainer?.querySelectorAll('input').forEach((item) => {
      item.removeEventListener('change', this.boundFacetChangeHandler);
    });

    this.facetsFormContainer?.querySelectorAll('.facet-clear--single').forEach((item) => {
      item.removeEventListener('click', this.boundFacetClearHandler);
    });

    // Clean up event listeners from main elements
    this.searchForm?.removeEventListener('submit', this.onFormSubmit);
    this.sortBy?.removeEventListener('change', this.onSelectChange);
    this.viewMoreButton?.removeEventListener('click', this.onViewMoreClick);
  }

  onFormSubmit(event) {
    event.preventDefault();
    this.clearFilters();
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  onSelectChange(event) {
    if (this.usingFallback) {
      // Let Shopify sorting handle it
      return;
    }
    const value = event.target.value;
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', value);
    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  onViewMoreClick(event) {
    event.preventDefault();
    const params = new URLSearchParams(window.location.search);

    // Update the current page
    this.currentPage++;
    params.set('page', this.currentPage);

    // Remove legacy 'from' parameter if it exists
    params.delete('from');

    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  updateQuery(params = null) {
    params = params ? params : new URLSearchParams(window.location.search);
    if (this.context == 'serp') {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;
      this.searchTerm = searchTerm;
      params.set('q', searchTerm);
    }
    const queryStr = params.toString();
    const url = !queryStr ? window.location.pathname : `${window.location.pathname}?${queryStr}`;
    window.history.replaceState({ path: url }, '', url);
  }

  clearFilters() {
    const params = new URLSearchParams(window.location.search);
    this.activeMaxPrice = null;
    for (const [param, value] of params.entries()) {
      if (param.startsWith('filter.')) {
        params.delete(param);
      }
    }
    // Reset to page 1 when clearing filters
    params.delete('page');
    this.currentPage = 1;
    this.updateQuery(params);
  }

  getQuery() {
    return this.input.value.trim();
  }

  /**
   * Get search results from cache, Nosto API or fallback to Shopify
   */
  async getSearchResults() {
    // Update header text for search context
    if (this.context === 'serp' && this.header) {
      this.header.textContent = `${window.nostoStrings?.searchResultsFor || 'Search results for'} "${this.searchTerm}"`;
    }
    const cacheKey = location.search || location.href;

    // Try to get cached results first for better performance
    if (!this.usingFallback && this.cachedResults[cacheKey]) {
      this.renderSearchResults(this.cachedResults[cacheKey]);
      return;
    }

    // Check if we should skip Nosto due to previous failures
    this.checkNostoAvailability();

    if (this.usingFallback && this.enableFallback) {
      return this.fallbackToShopify();
    }

    try {
      // Create a new abort controller for this request
      this.abortController = new AbortController();

      // Get Nosto search results
      // const nostoResults = await this.results;
      const nostoResults = await window.preloadedNostoResults;

      if (nostoResults?.products?.hits) {
        // Cache the results for faster subsequent requests
        this.cachedResults[cacheKey] = nostoResults;
        this.renderSearchResults(nostoResults);

        // Reset retry counter on success
        sessionStorage.removeItem('fallbackAttempts');
      } else {
        // Handle empty results
        this.renderNoResults();

        // Reset retry counter as this is a valid response (just empty)
        sessionStorage.removeItem('fallbackAttempts');
      }
    } catch (error) {
      console.error('Nosto search error:', error);

      // Get current attempt count from sessionStorage
      const currentAttempts = parseInt(sessionStorage.getItem('fallbackAttempts') || '0');
      const newAttempts = currentAttempts + 1;

      // Store updated attempt count
      sessionStorage.setItem('fallbackAttempts', String(newAttempts));

      console.log(`Nosto API attempt ${newAttempts} of ${this.maxRetries} failed`);

      if (newAttempts >= this.maxRetries) {
        console.log('Max attempts reached, setting skipNostoUntil and falling back to Shopify');

        if (this.enableFallback) {
          // Set skipNostoUntil in sessionStorage for 10 minutes
          sessionStorage.setItem('skipNostoUntil', String(Date.now() + 600000));
          return this.fallbackToShopify();
        } else {
          // Show error state if fallback is disabled
          this.removeLoadingState();
          this.renderErrorState();
        }
      } else {
        // Immediately fallback to Shopify without retrying Nosto
        if (this.enableFallback) {
          return this.fallbackToShopify();
        } else {
          // Show error state if fallback is disabled
          this.removeLoadingState();
          this.renderErrorState();
        }
      }
      return; // Exit early to avoid updating pagination state
    }

    // Update pagination state after results are loaded
    this.updatePaginationState();
  }

  /**
   * Check if Nosto should be skipped based on previous failures
   */
  checkNostoAvailability() {
    const skipNostoUntil = sessionStorage.getItem('skipNostoUntil');
    const currentTime = Date.now();

    if (skipNostoUntil && currentTime <= parseInt(skipNostoUntil)) {
      this.usingFallback = true;
      // Reset retry counter when using fallback
      this.retryCount = 0;
    } else {
      this.usingFallback = false;
      // Clear expired setting
      if (skipNostoUntil) {
        sessionStorage.removeItem('skipNostoUntil');
      }
    }
  }

  async fallbackToShopify() {
    this.usingFallback = true;

    try {
      // Instead of fetching HTML, redirect to the same URL with view=fallback parameter
      const currentParams = new URLSearchParams(window.location.search);

      // Add the view parameter to trigger the fallback template
      currentParams.set('view', 'fallback');

      // If we're on search page and have a search term, make sure it's in the params
      if (this.context === 'serp' && this.searchTerm && !currentParams.has('q')) {
        currentParams.set('q', this.searchTerm);
      }

      // Construct the fallback URL
      const fallbackUrl = `${window.location.pathname}?${currentParams.toString()}`;

      // Redirect to the fallback URL
      window.location.href = fallbackUrl;
    } catch (err) {
      console.error('Shopify fallback error:', err);
      // In case of an error with redirection, show an error message
      this.renderErrorState();
    }
  }

  async getNostoSearchResults() {
    const params = new URLSearchParams(window.location.search);
    const isKeyword = params.get('keyword') == 'true' ? true : false;
    const filters = [];
    const sortBy = [];

    if (params.has('sort_by')) {
      const sortValue = params.get('sort_by');
      // Skip relevance, since it's the default sort
      if (sortValue !== 'relevance') {
        let field = sortValue;
        let order = 'asc';
        if (sortValue.endsWith('-ascending')) {
          field = sortValue.replace('-ascending', '');
          order = 'asc';
        } else if (sortValue.endsWith('-descending')) {
          field = sortValue.replace('-descending', '');
          order = 'desc';
        }
        if (!field.includes('price')) {
          field = `stats.${field}`;
        }
        sortBy.push({
          field: field,
          order: order
        });
      }
    }

    // Calculate 'from' based on current page
    const currentPage = parseInt(params.get('page')) || 1;
    const from = (currentPage - 1) * this.resultsSize;

    // Iterate through all params to build filters array
    for (const [param, value] of params.entries()) {
      // Skip non-filter params
      if (!param.startsWith('filter.') || filters.some(f => f.field === 'price')) continue;
      if (param === 'filter.price') {
        const [range, max] = value.split('~');
        const [gte, lte] = range.split('-');
        filters.push({
          field: 'price',
          range: { lte: lte, gte: gte }
        });
      } else {
        // Remove filter. prefix to get param name
        const paramName = param.replace('filter.', '').toLowerCase();
        // Get the corresponding API field from window.filterToNosto
        const apiField = window.filterToNosto && window.filterToNosto[paramName] ? window.filterToNosto[paramName] : paramName;
        if (!apiField) {
          console.error(`Invalid filter parameter: ${paramName}`);
          continue;
        };
        // Split value string into array by pipe character
        const valueArray = value.split('|');
        filters.push({
          field: apiField,
          value: valueArray
        });
      }
    }
    try {
      const searchParams = {
        products: {
          facets: ['*'],
          fields: ['name', 'imageUrl', 'price', 'url', 'brand', 'listPrice', 'customFields.key', 'customFields.value'],
          size: this.resultsSize,
          from: from // Use calculated 'from' value
        }
      };

      const trackParams = {
        redirect: true,
        track: this.context,
        isKeyword: isKeyword
      }

      if (this.context === 'serp') {
        searchParams.query = this.searchTerm;
      } else if (this.context === 'category') {
        if (location.pathname !== '/collections/all') {
          searchParams.products.categoryId = this.collectionId;
          searchParams.products.categoryPath = this.collectionTitle;
        }
      }

      if (filters.length > 0) {
        searchParams.products.filter = filters;
      }

      if (sortBy.length > 0) {
        searchParams.products.sort = sortBy;
      }

      window.preloadedNostoResults = new Promise(resolve => {
        nostojs(async api => {
          if (this.context === 'serp') {
            api.recordSearchSubmit(searchParams.query)
          }
          const results = api.search(searchParams, trackParams)

          if (!results || results.status) {
            throw new Error('Invalid Nosto API response');
          }
          resolve(results);
        });
      });

    } catch (error) {
      console.log(error)
      throw error;
    }
  }

  async renderSearchResults(results) {
    this.classList.remove('no-results')
    const products = results.products.hits;
    const resultsFrom = results.products.from;
    const resultsTotal = results.products.total;
    if (resultsTotal <= resultsFrom + this.resultsSize) {
      this.viewMoreButton.style.display = 'none';
    } else {
      this.viewMoreButton.style.display = 'block';
    }

    if (!products || products.length === 0) {
      this.renderNoResults();
      return;
    }
    this.processExcludeFacets(products[0]);
    if (this.context === 'serp') {
      document.title = `Search: ${resultsTotal} results found for "${this.searchTerm}"`;
    }
    this.resultsCount.textContent = `${resultsTotal} ${window.nostoStrings.searchResults}`;
    if (this.showCount) {
      const showFrom = resultsFrom + this.resultsSize;
      const showCount = showFrom > resultsTotal ? resultsTotal : showFrom;
      const countText = window.nostoStrings.searchShowCount || 'Showing {{ count }} of {{ total }} items';
      this.showCount.textContent = countText
        .replace('{{ count }}', showCount)
        .replace('{{ total }}', resultsTotal);
    }
    this.productGrid.setAttribute('data-filtered-count', results.products.total);
    this.renderFacets(results.products.facets);
    const productsMarkup = await this.generateProductsMarkup(products);

    if (resultsFrom > 0) {
      // For pagination/load more - append new content
      this.productGrid.innerHTML += productsMarkup;
    } else {
      // For initial load or filter change - replace content
      this.productGrid.innerHTML = productsMarkup;
    }

    // Check if content slot is available in options
    const contentSlot = this.options.contentSlot;
    if (contentSlot) {
      const contentSlotElem = this._generateContentSlotElement(contentSlot);
      const refNode = this.productGrid.querySelector(`.card--product[data-index="${contentSlot.position}"]`) ?? this.productGrid.lastChild;
      this.productGrid.insertBefore(contentSlotElem, refNode);
    }

    // Add event listeners for image loading
    this.setupImageLoadingEvents();

    // Replace nr of results in document title
    this.removeLoadingState();
  }

  processExcludeFacets(product) {
    const customFields = product.customFields;
    if (this.includeFacetRules && this.context === 'category') {
      let division;
      if (Array.isArray(customFields)) {
        division = customFields.find(f => f.key === 'd365-division')?.value?.toLowerCase();
      }
      if (division && !this.includeFacetRules[division]) {
        // Push all facets to excludeFacets where the division key doesn't match
        Object.keys(this.includeFacetRules).forEach(key => {
          if (key !== division) {
            this.excludeFacets.push(this.includeFacetRules[key]);
          }
        });
      }
    }
  }

  // New method to handle image loading events
  setupImageLoadingEvents() {
    const images = this.productGrid.querySelectorAll('img:not(.loaded)');
    images.forEach(img => {
      if (img.complete) {
        img.classList.add('loaded');
      } else {
        img.addEventListener('load', () => {
          img.classList.add('loaded');
        });
      }
    });
  }

  /**
   * Display an error state when both Nosto and fallback fail
   */
  renderErrorState() {
    if (!this.productGrid) return;

    this.productGrid.innerHTML = `
          <div class="mt-gutter mb-gutter">
            <p role="status">There was an error retrieving search results. Please try again later.</p>
            <a class="link" href="/">${window.nostoStrings?.continueShopping || 'Continue shopping'}</a>
          </div>
        `;
  }

  async renderFacets(facets) {
    if (!this.facetsFormContainer || !facets || !Array.isArray(facets)) {
      console.warn('Cannot render facets: Missing required elements or data');
      return;
    }

    // Store open facets state before clearing
    this.saveOpenFacetsState();

    // Get template references
    const templates = this.getFacetTemplates();
    if (!templates.facetsDetails) {
      console.error('Required facet templates not found');
      return;
    }

    // Clear existing facets
    this.facetsFormContainer.innerHTML = '';

    // Process each facet
    facets.forEach(facet => this.renderSingleFacet(facet, templates));

    // Add event listeners to new facet elements
    this.attachFacetEventListeners();

    // Scroll to last edited facet if applicable
    this.scrollToLastEditedFacet();
  }

  // Helper functions for facet rendering
  saveOpenFacetsState() {
    const openDetails = this.facetsFormContainer?.querySelectorAll('details[open]') || [];
    this.openedFacets = Array.from(openDetails).map(detail =>
      detail.getAttribute('data-filter-group')
    ).filter(Boolean);
  }

  getFacetTemplates() {
    return {
      facetsDetails: document.querySelector('#nosto-facets-details-template'),
      facetsListItem: document.querySelector('#nosto-facets-list-item-template'),
      facetsColor: document.querySelector('#nosto-facets-color-swatches-template'),
      facetsRange: document.querySelector('#nosto-facets-range-template')
    };
  }

  renderSingleFacet(facet, templates) {
    if (!facet || !facet.name) return;
    if (this.excludeFacets.includes(facet.name.toLowerCase())) return;

    const content = templates.facetsDetails.content.cloneNode(true);
    const tempContainer = document.createElement('div');
    tempContainer.appendChild(content);

    const tempContainerContentElem = tempContainer.querySelector('[data-facets-container]');
    const filterLabel = tempContainer.querySelector('summary span');
    filterLabel.textContent = facet.name;

    // Set up details element
    const detailsElem = tempContainer.querySelector('details');
    detailsElem.setAttribute('data-filter-group', facet.name);

    // Restore open state if this facet was open before
    if (this.openedFacets.includes(facet.name)) {
      detailsElem.setAttribute('open', '');
    }

    let facetActive = false;

    // Handle different facet types
    if (facet.type === 'terms') {
      facetActive = this.renderTermsFacet(facet, tempContainerContentElem, templates);
    } else if (facet.type === 'stats' && facet.name === "Price") {
      facetActive = this.renderPriceFacet(facet, tempContainerContentElem, templates);
    }

    // Toggle clear button visibility
    const clearButton = tempContainer.querySelector('.facet-clear');
    if (clearButton) {
      clearButton.style.display = facetActive ? 'block' : 'none';
    }

    // Add to the DOM
    this.facetsFormContainer.appendChild(tempContainer.firstElementChild);
  }

  renderTermsFacet(facet, containerElem, templates) {
    let facetActive = false;

    // Determine filter type
    let filterType = 'default';
    if (facet.name === 'Colour') {
      filterType = 'color';
    }

    // Create list container
    const listContainer = document.createElement('ul');
    listContainer.classList.add('filter-options-list', filterType);
    containerElem.appendChild(listContainer);

    // Add each facet value
    facet.data.forEach(item => {
      const content = templates.facetsListItem.content.cloneNode(true);
      const listItemContainer = document.createElement('li');
      listItemContainer.classList.add('filter-option');
      listItemContainer.appendChild(content);

      // Get parameter name, using mapping if available
      const facetNameLower = facet.name.toLowerCase().replace(' ', '-');
      const paramName = window.nostoToFilter && window.nostoToFilter[facet.field]
        ? window.nostoToFilter[facet.field]
        : facetNameLower;

      if (!window.nostoToFilter[facet.field]) {
        window.filterToNosto[facetNameLower] = facet.field;
      }

      // Replace placeholders in template
      listItemContainer.innerHTML = listItemContainer.innerHTML
        .replace(/\[\[param_name\]\]/g, paramName)
        .replace(/\[\[value\]\]/g, item.value)
        .replace(/\[\[count\]\]/g, item.count)
        .replace(/\[\[filter_type\]\]/g, filterType);

      const input = listItemContainer.querySelector('input');
      const inputClassType = filterType === 'color' ? 'for-swatch' : 'checkbox';
      input.classList.add(inputClassType);

      // Handle color swatches
      if (filterType === 'color') {
        this.addColorSwatch(listItemContainer, item.value, templates);
      }

      // Set checked state
      if (item.selected) {
        input.checked = true;
        facetActive = true;
      } else {
        input.removeAttribute('checked');
      }

      listContainer.appendChild(listItemContainer);
    });

    // Add search input for facets that should be searchable
    if (this.searchableFacets.includes(facet.field.toLowerCase())) {
      this.renderFacetSearch(listContainer, facet.field);
    }

    return facetActive;
  }

  addColorSwatch(container, colorValue, templates) {
    const content = templates.facetsColor.content.cloneNode(true);
    const swatchContainer = document.createElement('span');
    swatchContainer.classList.add('swatch');
    swatchContainer.appendChild(content);

    // Get color value, using mapping if available
    let facetColor = colorValue.toLowerCase();
    if (window.colorPatterns && window.colorPatterns[facetColor]) {
      facetColor = window.colorPatterns[facetColor];
    }

    swatchContainer.innerHTML = swatchContainer.innerHTML
      .replace(/\[\[color\]\]/g, colorValue)
      .replace(/\[\[color_hex\]\]/g, facetColor);

    const spanLabel = container.querySelector('span[data-option-label]');
    container.querySelector('label').insertBefore(swatchContainer, spanLabel);
  }

  renderPriceFacet(facet, containerElem, templates) {
    const content = templates.facetsRange.content.cloneNode(true);
    const priceRangeTempContainer = document.createElement('div');
    priceRangeTempContainer.appendChild(content);

    const params = new URLSearchParams(window.location.search);
    const priceParam = params.get('filter.price');
    let newMinValue = 0;
    let newMaxValue = facet.max;
    let maxPrice = facet.max;
    let facetActive = false;

    if (priceParam) {
      const [range, max] = priceParam.split('~');
      const [gte, lte] = range.split('-');
      newMinValue = parseInt(gte) || 0;
      newMaxValue = parseInt(lte) || facet.max;
      maxPrice = parseInt(max) || facet.max;
      facetActive = true;
    } else {
      this.activeMaxPrice = facet.max;
    }

    priceRangeTempContainer.innerHTML = priceRangeTempContainer.innerHTML
      .replace(/\[\[min_value\]\]/g, newMinValue)
      .replace(/\[\[max_value\]\]/g, newMaxValue)
      .replace(/\[\[gte_max\]\]/g, maxPrice - 1)
      .replace(/\[\[lte_max\]\]/g, maxPrice)
      .replace(/\[\[lte_min\]\]/g, newMinValue + 1);

    containerElem.appendChild(priceRangeTempContainer);

    return facetActive;
  }

  attachFacetEventListeners() {
    // Add change event listeners to inputs
    this.facetsFormContainer.querySelectorAll('input').forEach((item) => {
      item.addEventListener('change', debounce(this.boundFacetChangeHandler, 150));
    });

    // Add click event listeners to clear buttons
    this.facetsFormContainer.querySelectorAll('.facet-clear').forEach((item) => {
      item.addEventListener('click', debounce(this.boundFacetClearHandler, 150));
    });

    // Initialize accordions for details elements
    this.facetsFormContainer.querySelectorAll('details').forEach((el) => {
      new Accordion(el);
    });
  }

  scrollToLastEditedFacet() {
    if (!this.lastEditedFacet) return;

    const lastEditedFacetInput = this.facetsFormContainer.querySelector(
      `.filter-option input[data-key-value="${this.lastEditedFacet}"]`
    );

    if (lastEditedFacetInput) {
      // Use smooth scrolling and add slight delay to ensure the facet is open
      setTimeout(() => {
        lastEditedFacetInput.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }

  renderFacetSearch(container, facetTitle) {
    const searchLabel = document.createElement('label');
    const inputTextContent = `${window.nostoStrings.search} ${facetTitle}s`;
    searchLabel.textContent = inputTextContent;
    searchLabel.classList.add('visually-hidden'); // Hides label visually but keeps it accessible

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.placeholder = inputTextContent;
    searchInput.classList.add('facet-search-field');
    searchInput.setAttribute('aria-label', inputTextContent);

    const searchWrapper = document.createElement('div');
    searchWrapper.classList.add('facet-search');
    searchWrapper.appendChild(searchLabel);
    searchWrapper.appendChild(searchInput);

    container.parentNode.insertBefore(searchWrapper, container);

    searchInput.addEventListener('input', (e) => {
      const searchValue = e.target.value.toLowerCase();
      const listItems = container.querySelectorAll('li');
      listItems.forEach(item => {
        const label = item.querySelector('[data-option-label]').textContent.toLowerCase();
        item.style.display = label.includes(searchValue) ? '' : 'none';
      });
    });
  }

  async onFacetChange(event) {
    if (this.usingFallback) {
      // Let Shopify facets handle it
      return;
    }
    const target = event.target;
    if (target.classList.contains('facet-search-field')) return;
    const params = new URLSearchParams(window.location.search);
    const isPriceRange = target.classList.contains('price-range__input');
    const paramTitle = target.getAttribute('data-param-name').toLowerCase();

    // Reset pagination when changing filters
    params.delete('page');
    this.currentPage = 1;

    if (isPriceRange) {
      // Every price facet change triggers events for both min and max inputs, skip the min input and handle only the event from max input
      // If selected value is out of bounds, remove filter value from parameters
      const inputMin = target.getAttribute('name') === 'filter.price.gte';
      const inputMax = target.getAttribute('name') === 'filter.price.lte';
      if (inputMin && target.value < Math.floor(target.min)) {
        target.value = target.min;
        return;
      } else if (inputMax && target.value > Math.floor(target.max)) {
        target.value = target.max;
        return;
      } else {
        const minInput = this.facetsFormContainer.querySelector('input[name="filter.price.gte"]');
        const maxInput = this.facetsFormContainer.querySelector('input[name="filter.price.lte"]');

        // Skip if values are out of bounds
        if (parseFloat(minInput.value) < parseFloat(minInput.min) ||
          parseFloat(maxInput.value) > parseFloat(maxInput.max)) {
          return;
        }

        // Construct the new price parameter
        const priceValue = `${minInput.value}-${maxInput.value}`;
        if (this.activeMaxPrice) {
          params.set('filter.price', `${priceValue}~${this.activeMaxPrice}`);
        } else {
          params.set('filter.price', priceValue);
        }
      }
    }

    const keyValue = target.getAttribute('data-key-value');
    const param = `filter.${paramTitle}`;
    const value = target.getAttribute('data-value') || target.value;

    if (!param || !value) return;

    const existingValues = params.get(param)?.split('|') || [];
    if (!isPriceRange) {
      if (target.checked) {
        existingValues.push(value);
        params.set(param, existingValues.join('|'));
        this.lastEditedFacet = keyValue;
      } else {
        const newValues = existingValues.filter(v => v !== value);
        if (newValues.length === 0) {
          params.delete(param);
          this.lastEditedFacet = null;
        } else {
          params.set(param, newValues.join('|'));
        }
      }
    }

    this.updateQuery(params);
    // Skip event from min price input, price change is handled from max input
    if (target.getAttribute('data-min') !== null) return;
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  async onFacetClear(event) {
    event.preventDefault();
    const target = event.target;
    const params = new URLSearchParams(window.location.search);
    const parent = target.closest('details');
    const field = parent.getAttribute('data-filter-group')?.toLowerCase();

    // Reset pagination when clearing filters
    params.delete('page');
    this.currentPage = 1;

    if (field == 'price') {
      params.delete('filter.price');
      this.activeMaxPrice = null;
    } else {
      const inputs = parent.querySelectorAll('input');
      inputs.forEach((input) => {
        input.checked = false;
      });

      params.delete(`filter.${field}`);
      this.lastEditedFacet = null;
    }

    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  renderNoResults() {
    if (!this.productGrid) return;

    const noResultsMessage = window.nostoStrings?.searchNoResults || 'No results for "{{terms}}"';
    const continueShoppingText = window.nostoStrings?.continueShopping || 'Continue shopping';
    this.classList.add('no-results');

    this.productGrid.innerHTML = `
          <div class="mt-gutter mb-gutter text-container">
            <p role="status">${noResultsMessage.replace('{{ terms }}', this.searchTerm || '')}</p>
            <a class="link" href="/">${continueShoppingText}</a>
          </div>
        `;
    this.removeLoadingState();

    // Update page title
    if (this.context === 'serp') {
      document.title = `Search: No results for "${this.searchTerm}"`;
    }
  }
}

if (!customElements.get('nosto-search')) {
  customElements.define('nosto-search', NostoSearch);
}
initNostoRecommendations();

// Listen for cart add events to refresh Nosto recommendations
document.addEventListener('ajaxProduct:added', updateNostoAfterCartAdd);
